/**
 * Custom error class for BizNest client-side errors.
 * @extends Error
 */
export class BizNestClientError extends Error {
  /**
   * Creates a new BizNestClientError instance.
   * @param message - Error message
   */
  constructor(message: string) {
    super(message);
    this.name = 'BizNestClientError';
    Error.captureStackTrace(this, this.constructor);
  }
}

/**
 * Error class for validation-related errors in the BizNest client.
 * @extends BizNestClientError
 */
export class BizNestClientValidationError extends BizNestClientError {
  /** The field that failed validation, if applicable */
  public field?: string;

  /**
   * Creates a new BizNestClientValidationError instance.
   * @param message - Error message
   * @param field - The field that failed validation (optional)
   */
  constructor(message: string, field?: string) {
    super(message);
    this.name = 'BizNestClientValidationError';
    this.field = field;
    Error.captureStackTrace(this, this.constructor);
  }

  /**
   * Creates a validation error for a missing required field.
   * @param field - The name of the missing field
   * @returns A new validation error instance
   */
  static fromMissingField(field: string): BizNestClientValidationError {
    return new BizNestClientValidationError(`Missing required field: ${field}`, field);
  }

  /**
   * Creates a validation error for an invalid field value.
   * @param field - The name of the field with invalid value
   * @param reason - Description of why the value is invalid
   * @returns A new validation error instance
   */
  static fromInvalidValue(field: string, reason: string): BizNestClientValidationError {
    return new BizNestClientValidationError(
      `Invalid value for field ${field}: ${reason}`,
      field
    );
  }
}

/**
 * Error class for request-related errors in the BizNest client.
 * @extends BizNestClientError
 */
export class BizNestClientRequestError extends BizNestClientError {
  /** Error code, if available */
  public code?: string;

  /** HTTP status code, if available */
  public status?: number;

  /**
   * Creates a new BizNestClientRequestError instance.
   * @param message - Error message
   * @param code - Error code (optional)
   * @param status - HTTP status code (optional)
   */
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'BizNestClientRequestError';
    this.code = code;
    this.status = status;
    Error.captureStackTrace(this, this.constructor);
  }
}

/**
 * Validates data against a set of type and validation rules.
 * @template T - Type of the data object being validated
 * @param data - The data object to validate
 * @param typeChecks - Object defining validation rules for each field
 * @throws {BizNestClientValidationError} If validation fails
 *
 * @example
 * ```typescript
 * const user = { name: 'John', age: 25 };
 * validateData(user, {
 *   name: { required: true, validate: (val) => typeof val === 'string' },
 *   age: {
 *     required: true,
 *     validate: (val) => Number.isInteger(val) && val >= 0,
 *     message: 'must be a non-negative integer'
 *   }
 * });
 * ```
 */
export function validateData<T>(
  data: T,
  typeChecks: {
    [K in keyof T]?: {

      /** Whether the field is required (default: true) */
      required?: boolean;

      /** Validation function that returns true if the value is valid */
      validate: (value: T[K]) => boolean;

      /** Custom error message for validation failures */
      message?: string;
    }
  }
): void {
  for (const [field, rules] of Object.entries(typeChecks)) {
    const value = data[field as keyof T];
    const { required = true, validate, message } = rules as {
      required?: boolean;
      validate: (value: unknown) => boolean;
      message?: string;
    };

    if (required && (value === undefined || value === null)) {
      throw BizNestClientValidationError.fromMissingField(field);
    }

    if (value === undefined || value === null) {
      continue;
    }

    if (!validate(value)) {
      throw BizNestClientValidationError.fromInvalidValue(
        field,
        message || 'failed type validation'
      );
    }
  }
}
